// ---------------------------------------------------------------------------
// SubjectCollection card layout
// ---------------------------------------------------------------------------
//
// A collection card is a *grid container* — the user controls how many
// columns are visible by resizing the card horizontally, and how many rows
// are visible by resizing it vertically. Variants flow left-to-right then
// top-to-bottom; new variants always claim the next empty slot, growing
// into a new row when needed.
//
// Sizes are derived from a small fixed tile geometry so:
//  - The card's pixel width is `inset + cols * tileSize + (cols-1) * gap + inset`
//  - The card's pixel height is `header + inset + rows * (tile + label) + (rows-1) * gap + inset`
//
// Both `cols` and `rows` are integers, and the resize handler snaps the
// dragged dimension to the nearest integer boundary. This module owns the
// math so the engine, the view, and the yjs persistence layer all agree.

// --- Tile geometry (constant, drives card sizing) ---------------------------

export const COLLECTION_HEADER_HEIGHT = 32
export const COLLECTION_TILE_SIZE = 68
export const COLLECTION_TILE_GAP_X = 8
export const COLLECTION_TILE_GAP_Y = 16
export const COLLECTION_TILE_LABEL_HEIGHT = 26
export const COLLECTION_TILE_INSET = 12

/** Horizontal pitch of one tile column (tile + inter-tile gap). */
export const COLLECTION_TILE_STEP_X = COLLECTION_TILE_SIZE + COLLECTION_TILE_GAP_X

/** Vertical pitch of one tile row (tile + label + inter-row gap). */
export const COLLECTION_TILE_STEP_Y = COLLECTION_TILE_SIZE + COLLECTION_TILE_LABEL_HEIGHT + COLLECTION_TILE_GAP_Y

// --- Column / row bounds ----------------------------------------------------

export const COLLECTION_DEFAULT_COLUMNS = 3
export const COLLECTION_MIN_COLUMNS = 1
/** Cap kept generous (8) so wide-format galleries fit without forcing the
 *  user to scroll; beyond this width the card competes with bins for
 *  visual real estate and reads as "I should have used a bin instead". */
export const COLLECTION_MAX_COLUMNS = 8

export const COLLECTION_MIN_ROWS = 1
/** Upper bound for a *manual* vertical resize (see {@link snapHeightToRows} /
 *  {@link maxRowsThatFit}). It does NOT cap the content-driven height: a card
 *  always grows tall enough to give every variant a visible slot
 *  ({@link getCollectionCardHeightForRows}), otherwise tiles would paint
 *  outside the card frame. A future vertical-scroll affordance can reinstate
 *  a hard height cap. */
export const COLLECTION_MAX_ROWS = 10

// --- Pure pixel-math helpers ------------------------------------------------

/** Pixel width of a card configured to display `cols` tile columns. */
export function getCollectionCardWidth(cols: number): number {
  const c = clampColumns(cols)
  return COLLECTION_TILE_INSET + c * COLLECTION_TILE_SIZE + (c - 1) * COLLECTION_TILE_GAP_X + COLLECTION_TILE_INSET
}

/**
 * Pixel height of a card configured to display `rows` tile rows.
 *
 * Floors at {@link COLLECTION_MIN_ROWS} but is intentionally NOT capped at
 * {@link COLLECTION_MAX_ROWS}: callers pass `max(userRows, minRowsForItems)`,
 * and a collection with more variants than `MAX_ROWS * cols` must still grow
 * tall enough to contain every tile. Capping here would let tiles spill below
 * the card border. `MAX_ROWS` bounds the user's manual drag instead (it is
 * applied to `userRows` via {@link snapHeightToRows} / {@link maxRowsThatFit}).
 */
export function getCollectionCardHeightForRows(rows: number): number {
  const r = Number.isFinite(rows) ? Math.max(COLLECTION_MIN_ROWS, Math.round(rows)) : COLLECTION_MIN_ROWS
  return (
    COLLECTION_HEADER_HEIGHT +
    COLLECTION_TILE_INSET +
    r * (COLLECTION_TILE_SIZE + COLLECTION_TILE_LABEL_HEIGHT) +
    (r - 1) * COLLECTION_TILE_GAP_Y +
    COLLECTION_TILE_INSET
  )
}

/** Minimum row count needed so every variant has a visible slot. */
export function getMinRowsForVariantCount(variantCount: number, cols: number): number {
  const c = clampColumns(cols)
  return Math.max(COLLECTION_MIN_ROWS, Math.ceil(Math.max(0, variantCount) / c))
}

export function clampColumns(cols: number): number {
  if (!Number.isFinite(cols)) return COLLECTION_DEFAULT_COLUMNS
  return Math.max(COLLECTION_MIN_COLUMNS, Math.min(COLLECTION_MAX_COLUMNS, Math.round(cols)))
}

export function clampRows(rows: number): number {
  if (!Number.isFinite(rows)) return COLLECTION_MIN_ROWS
  return Math.max(COLLECTION_MIN_ROWS, Math.min(COLLECTION_MAX_ROWS, Math.round(rows)))
}

/** Snap an arbitrary pixel width down/up to the nearest valid column count. */
export function snapWidthToColumns(width: number): number {
  if (!Number.isFinite(width) || width <= 0) return COLLECTION_DEFAULT_COLUMNS
  // Inverse of getCollectionCardWidth: solve for `cols` then round.
  const inner = width - 2 * COLLECTION_TILE_INSET + COLLECTION_TILE_GAP_X
  const cols = inner / COLLECTION_TILE_STEP_X
  return clampColumns(cols)
}

/** Snap an arbitrary pixel height down/up to the nearest valid row count. */
export function snapHeightToRows(height: number): number {
  if (!Number.isFinite(height) || height <= 0) return COLLECTION_MIN_ROWS
  const inner = height - COLLECTION_HEADER_HEIGHT - 2 * COLLECTION_TILE_INSET + COLLECTION_TILE_GAP_Y
  const rows = inner / COLLECTION_TILE_STEP_Y
  return clampRows(rows)
}

/**
 * Max integer columns whose natural tile grid fits inside `width`. Used by
 * the resize handler so tiles only reflow into N columns when there's
 * actually room — i.e., reflow thresholds line up with natural widths
 * (244 → 320 → 396 …) rather than with halfway midpoints. Gives a
 * predictable "drag to natural width to unlock more cols" feel.
 */
export function maxColumnsThatFit(width: number): number {
  if (!Number.isFinite(width) || width <= 0) return COLLECTION_MIN_COLUMNS
  // floor((width - 2*inset + gap) / step_x) — symmetric inverse of
  // getCollectionCardWidth's formula, but with `floor` instead of `round`.
  const cols = Math.floor((width - 2 * COLLECTION_TILE_INSET + COLLECTION_TILE_GAP_X) / COLLECTION_TILE_STEP_X)
  return Math.max(COLLECTION_MIN_COLUMNS, Math.min(COLLECTION_MAX_COLUMNS, cols))
}

/** Max integer rows whose natural tile grid fits inside `height`. */
export function maxRowsThatFit(height: number): number {
  if (!Number.isFinite(height) || height <= 0) return COLLECTION_MIN_ROWS
  const rows = Math.floor(
    (height - COLLECTION_HEADER_HEIGHT - 2 * COLLECTION_TILE_INSET + COLLECTION_TILE_GAP_Y) / COLLECTION_TILE_STEP_Y,
  )
  return Math.max(COLLECTION_MIN_ROWS, Math.min(COLLECTION_MAX_ROWS, rows))
}

// --- High-level resolver (used by yjs → engine + by the view) ---------------

/**
 * Resolve a card's final column / row layout from its persisted pixel
 * dimensions plus the current variant count. Returns the integer cols/rows
 * plus the pixel width/height the view should draw at.
 *
 * - When `persistedWidth` or `persistedHeight` is missing/zero, the
 *   default 3-column layout is used and the height auto-grows to fit
 *   every variant.
 * - When persisted dimensions exist, they're snapped to grid boundaries.
 *   The final row count is `max(userRows, ceil(variants/cols))` so
 *   resizing a card smaller can never visually hide a variant.
 */
export interface CollectionLayout {
  columns: number
  rows: number
  width: number
  height: number
}

export function resolveCollectionLayout(args: {
  variantCount: number
  persistedWidth?: number
  persistedHeight?: number
}): CollectionLayout {
  // Nearest-snap is the canonical "where did the body land?" calculation —
  // it matches what `handleCollectionResizeEnd` writes on release, so a
  // round-trip through Yjs is the identity. For legacy off-grid widths
  // (e.g. pre-resizable-card data) the snap also pulls them onto the
  // grid the first time the file opens, giving every collection a clean
  // size after one save.
  const cols = args.persistedWidth ? snapWidthToColumns(args.persistedWidth) : COLLECTION_DEFAULT_COLUMNS
  const userRows = args.persistedHeight
    ? snapHeightToRows(args.persistedHeight)
    : getMinRowsForVariantCount(args.variantCount, cols)
  const minRows = getMinRowsForVariantCount(args.variantCount, cols)
  const rows = Math.max(userRows, minRows)
  return {
    columns: cols,
    rows,
    width: getCollectionCardWidth(cols),
    height: getCollectionCardHeightForRows(rows),
  }
}
